import { useTrackPromotion } from '@/hooks/useTrackPromotion';
import type { Ga4Promotion } from '@/types';
import { buildCloudflareImageUrl } from '@/utils/buildCloudflareImageUrl';
import { slugify } from '@/utils/slugify';
import { useRef, useState } from 'react';
import {
  Button as AriaButton,
  Modal as AriaModal,
  Dialog,
  DialogTrigger,
  ModalOverlay,
} from 'react-aria-components';

interface MediaTile {
  kind: 'image' | 'video';
  imageUrl: string | null;
  altText: string | null;
  videoUrl: string | null;
  posterUrl: string | null;
  captionsUrl: string | null;
  ctaText: string | null;
  link: string | null;
}

interface MediaBlockData {
  key: string;
  headline?: string;
  layout: string;
  tiles: MediaTile[];
}

const MEDIA_SLOT = 'media-block';

// Only linked image tiles are promotions: videos are engagement,
// and an image with no destination isn't a clickable promo. Mirrors the
// BrandCarousel promotion mapping.
const tilePromotion = (
  tile: MediaTile,
  index: number,
  headline?: string,
): Ga4Promotion | null => {
  const isVideo = tile.kind === 'video' && !!tile.videoUrl;
  if (isVideo || !tile.link) {
    return null;
  }
  const name =
    tile.ctaText ?? tile.altText ?? `${headline ?? MEDIA_SLOT} ${index + 1}`;
  return {
    promotion_id: slugify(name) || `${MEDIA_SLOT}-${index}`,
    promotion_name: name,
    creative_name: headline ?? MEDIA_SLOT,
    creative_slot: MEDIA_SLOT,
    index,
  };
};

/**
 * N-FP-06 Media Block. The server block (Views/Shared/Components/MediaBlock/Default.cshtml) emits a
 * `data-react-component="MediaBlock"` mount with the tiles as JSON; SiteApp's useComponentFactory
 * hydrates it. Each tile is an image or a video — videos with a poster open in a lightbox
 * ("triggers modal playback"), otherwise they play inline. Hosted files use the native <video>
 * controls; YouTube/Vimeo URLs become provider <iframe> embeds.
 */

// YouTube / Vimeo → an embeddable iframe URL (native provider controls). Anything else is treated as
// a hosted file and played through a native <video> element.
const toEmbedUrl = (url: string): string | null => {
  const yt = url.match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/,
  );
  if (yt) {
    return `https://www.youtube.com/embed/${yt[1]}`;
  }

  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) {
    return `https://player.vimeo.com/video/${vimeo[1]}`;
  }

  return null;
};

const isVtt = (url: string): boolean => /\.vtt(\?|#|$)/i.test(url);

const withAutoplay = (embedUrl: string): string =>
  embedUrl + (embedUrl.includes('?') ? '&autoplay=1' : '?autoplay=1');

const Player = ({
  tile,
  autoPlay,
}: { tile: MediaTile; autoPlay?: boolean }) => {
  const embedUrl = tile.videoUrl ? toEmbedUrl(tile.videoUrl) : null;

  if (embedUrl) {
    return (
      <div className="nfp06-video">
        <iframe
          className="nfp06-embed"
          src={autoPlay ? withAutoplay(embedUrl) : embedUrl}
          title={tile.altText ?? 'Video'}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  const hasTrack = !!tile.captionsUrl && isVtt(tile.captionsUrl);
  const hasTranscript = !!tile.captionsUrl && !isVtt(tile.captionsUrl);

  return (
    <div className="nfp06-video">
      {/* eslint-disable-next-line jsx-a11y/media-has-caption -- transcript link covers the no-VTT case */}
      <video
        className="nfp06-video-el"
        controls
        preload="metadata"
        autoPlay={autoPlay}
        poster={
          tile.posterUrl
            ? buildCloudflareImageUrl(tile.posterUrl, { width: 1200 })
            : undefined
        }
      >
        <source src={tile.videoUrl ?? undefined} />
        {hasTrack ? (
          <track
            default
            kind="captions"
            label="English"
            srcLang="en"
            src={tile.captionsUrl ?? undefined}
          />
        ) : null}
        Your browser does not support this video.
      </video>
      {hasTranscript ? (
        <a className="nfp06-transcript" href={tile.captionsUrl ?? undefined}>
          View transcript
        </a>
      ) : null}
    </div>
  );
};

const VideoTile = ({ tile }: { tile: MediaTile }) => {
  // No poster → inline playback.
  if (!tile.posterUrl) {
    return <Player tile={tile} />;
  }

  // Poster set → modal (lightbox) playback.
  return (
    <DialogTrigger>
      <AriaButton
        className="nfp06-poster"
        aria-label={tile.altText ? `Play video: ${tile.altText}` : 'Play video'}
      >
        <img
          src={buildCloudflareImageUrl(tile.posterUrl, { width: 1200 })}
          alt={tile.altText ?? ''}
          loading="lazy"
          decoding="async"
        />
        <span className="nfp06-play" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7z" />
          </svg>
        </span>
      </AriaButton>

      <ModalOverlay className="nfp06-modal-overlay" isDismissable>
        <AriaModal className="nfp06-modal">
          <Dialog className="nfp06-dialog" aria-label="Video player">
            {({ close }) => (
              <>
                <button
                  type="button"
                  className="nfp06-modal-close"
                  onClick={close}
                  aria-label="Close video"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                  >
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </button>
                <Player tile={tile} autoPlay />
              </>
            )}
          </Dialog>
        </AriaModal>
      </ModalOverlay>
    </DialogTrigger>
  );
};

const Chip = ({
  text,
  link,
  onClick,
}: { text: string; link: string | null; onClick?: () => void }) =>
  link ? (
    <a className="nfp06-chip" href={link} onClick={onClick}>
      {text}
    </a>
  ) : (
    <span className="nfp06-chip nfp06-chip--static">{text}</span>
  );

const ImageTile = ({
  tile,
  promotion,
  onSelect,
}: {
  tile: MediaTile;
  promotion: Ga4Promotion | null;
  onSelect: (promotion: Ga4Promotion) => void;
}) => {
  const [failed, setFailed] = useState(false);
  const select = promotion ? () => onSelect(promotion) : undefined;

  // Graceful fallback: a tile whose image is missing or fails to load is dropped rather than
  // rendering a broken image.
  if (!tile.imageUrl || failed) {
    return null;
  }

  const img = (
    <img
      className="nfp06-img"
      src={buildCloudflareImageUrl(tile.imageUrl, { width: 1200 })}
      alt={tile.altText ?? ''}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
    />
  );

  return (
    <>
      {tile.link ? (
        <a
          className="nfp06-img-link"
          href={tile.link}
          aria-label={tile.altText ?? undefined}
          onClick={select}
        >
          {img}
        </a>
      ) : (
        img
      )}
      {tile.ctaText ? (
        <Chip text={tile.ctaText} link={tile.link} onClick={select} />
      ) : null}
    </>
  );
};

export const MediaBlock = ({ data }: { data: MediaBlockData }) => {
  const tiles = data.tiles ?? [];
  const gridRef = useRef<HTMLDivElement>(null);

  // One promotion per linked image tile (null for videos / unlinked images).
  const promotions = tiles.map((tile, index) =>
    tilePromotion(tile, index, data.headline),
  );
  const { onSelect } = useTrackPromotion(
    gridRef,
    promotions.filter((promo): promo is Ga4Promotion => promo !== null),
  );

  if (tiles.length === 0) {
    return null;
  }

  return (
    <div className="nfp06-grid" ref={gridRef}>
      {tiles.map((tile, index) => (
        <div className="nfp06-tile" key={`${data.key}-${index}`}>
          {tile.kind === 'video' && tile.videoUrl ? (
            <VideoTile tile={tile} />
          ) : (
            <ImageTile
              tile={tile}
              promotion={promotions[index]}
              onSelect={onSelect}
            />
          )}
        </div>
      ))}
    </div>
  );
};
